/**
 * miroExcursionApi — thin client for the integrations engine's semantic
 * excursion routes (engines/integrations-engine/src/routes/miro.ts).
 *
 * Lives beside the other semanticConnectors modules rather than inside
 * IntegrationsApiClient so the excursion surface stays lint-clean and
 * self-contained; it shares the engine base URL via engineUrls.
 */

import { INTEGRATIONS_ENGINE_URL } from '../engineUrls';

/**
 * Dialect-shaped read of a Miro board's full semantic surface for excursion
 * return (all item types + connectors + frames). The SemanticSnapshot is
 * built client-side from this via the connector profile + workspace maps.
 */
export interface MiroExcursionBoardRead {
  board: { id: string; name?: string; viewLink?: string };
  items: Array<{
    id: string;
    type: string;
    text: string;
    description?: string;
    fillColor?: string;
    shape?: string;
    x: number;
    y: number;
    parentFrameId?: string;
  }>;
  connectors: Array<{
    id: string;
    startItemId?: string;
    endItemId?: string;
    caption: string;
    strokeStyle: string;
  }>;
  frames: Array<{ id: string; title: string; x: number; y: number; width: number; height: number }>;
}

export interface MiroExcursionExportInput {
  workspaceId: string;
  boardName?: string;
  boardId?: string;
  frameTitle?: string;
  objects: Array<{ internalId: string; title: string; description?: string; fillColor?: string; kind: 'sticky' | 'card' }>;
  relations: Array<{ sourceInternalId: string; targetInternalId: string; caption?: string; strokeStyle?: 'normal' | 'dashed' | 'dotted' }>;
}

export interface MiroExcursionExportResult {
  boardId: string;
  boardUrl: string;
  frameId: string | null;
  objects: Array<{ internalId: string; externalId: string; nativeType: string }>;
  connectors: Array<{ externalId: string; sourceInternalId: string; targetInternalId: string }>;
  errors: string[];
}

async function engineRequest<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${INTEGRATIONS_ENGINE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const payload = (await response.json().catch(() => ({}))) as T & { success?: boolean; error?: string; details?: string };
  if (!response.ok || payload.success === false) {
    throw new Error(payload.error ?? payload.details ?? `Integrations engine error ${response.status}`);
  }
  return payload;
}

/**
 * Departure leg (05-VERTICAL-SLICE §VS-1): export objects + canonical
 * relations to a Miro board (scope frame, stickies/cards, captioned
 * connectors). Returns created external ids for identity registration.
 */
export async function miroExcursionExport(input: MiroExcursionExportInput): Promise<MiroExcursionExportResult> {
  const result = await engineRequest<MiroExcursionExportResult>('POST', '/miro/excursions/export', input);
  return { ...result, errors: result.errors ?? [] };
}

/**
 * Return leg (§VS-2): read the board's full semantic surface in dialect
 * shape. The caller builds the SemanticSnapshot via profile + maps.
 */
export async function miroExcursionReadBoard(workspaceId: string, boardId: string): Promise<MiroExcursionBoardRead> {
  return engineRequest<MiroExcursionBoardRead>(
    'GET',
    `/miro/excursions/board/${encodeURIComponent(boardId)}?workspaceId=${encodeURIComponent(workspaceId)}`,
  );
}
